import { v4 as Uuid } from 'uuid'
import { jclone } from '@/etc/utils'
import { LOG } from '@/etc/log'
import { VIEW_MAX_PADDING, ZOOM_MAX_SEC, ZOOM_STEP } from '@/etc/consts'
import { factory } from '../state'

const log = LOG.m('store::project::timeline')
const TEXT_SMART_TRANSFORM_PROPS = new Set([
  'position.x',
  'position.y',
  'scale.x',
  'scale.y',
  'rotation.z',
  'opacity',
])
const TEXT_SMART_PARTS = ['start', 'left', 'right', 'end']

const textSmartDefaultValue = (prop, fallback = null) => {
  if (typeof fallback === 'number') return fallback
  if (prop === 'opacity') return 1
  if (prop.startsWith('scale.')) return 1
  return 0
}

const syncTextTransformToSmartKeys = (strip, updates, { part = null, baseValues = {}, tool = null, delta = null } = {}) => {
  if (!strip || strip.asset?.type !== 'text') return false
  const touched = Object.keys(updates || {}).filter((prop) => TEXT_SMART_TRANSFORM_PROPS.has(prop))
  if (!touched.length) return false

  if (!strip.smartKeys || typeof strip.smartKeys !== 'object') strip.smartKeys = {}

  const targetParts = TEXT_SMART_PARTS.includes(part) ? [part] : TEXT_SMART_PARTS
  const translateDelta = {
    'position.x': Number.isFinite(Number(delta?.x)) ? Number(delta.x) : null,
    'position.y': Number.isFinite(Number(delta?.y)) ? Number(delta.y) : null,
  }
  const applyWholeStripTranslationDelta =
    !TEXT_SMART_PARTS.includes(part) &&
    tool === 'Translate2D'

  touched.forEach((prop) => {
    const nextValue = updates[prop]
    if (typeof nextValue !== 'number') return

    const fallback = textSmartDefaultValue(prop, baseValues?.[prop])
    const current = strip.smartKeys[prop] || {
      start: { value: fallback },
      left: { value: fallback },
      right: { value: fallback },
      end: { value: fallback },
      offsets: { entry: 0, exit: 0 },
      links: { rightToLeft: false },
      sustainEasing: 'Linear.None',
      entryEasing: 'Linear.None',
      exitEasing: 'Linear.None',
    }
    const linked = !!(current.links && current.links.rightToLeft)
    const nextGroup = {
      ...current,
    }

    targetParts.forEach((p) => {
      const currentPartValue = typeof nextGroup?.[p]?.value === 'number'
        ? nextGroup[p].value
        : fallback
      const nextPartValue =
        applyWholeStripTranslationDelta && Number.isFinite(translateDelta[prop])
          ? currentPartValue + translateDelta[prop]
          : nextValue
      nextGroup[p] = {
        ...(nextGroup[p] || { value: fallback }),
        value: nextPartValue,
      }
    })

    if (linked && targetParts.length === 1 && targetParts[0] === 'left') {
      nextGroup.right = {
        ...(nextGroup.right || { value: fallback }),
        value: nextValue,
      }
    }

    if (linked && targetParts.length === 1 && targetParts[0] === 'right') {
      nextGroup.links = {
        ...(nextGroup.links || {}),
        rightToLeft: false,
      }
    }

    strip.smartKeys[prop] = nextGroup
  })

  return true
}

const doAnimateTimeWindow = (state, { start, end, duration = 150, easing = null }) => {
  if (typeof window === 'undefined') {
    state.timeline.startTime = start
    state.timeline.endTime = end
    return
  }
  const totalLength = state.timeline.length || 0
  let viewLimit = Math.max(totalLength, state.timeline.viewMax || 0)
  const rawStart = Number.isFinite(start) ? start : state.timeline.startTime
  const rawEnd = Number.isFinite(end) ? end : state.timeline.endTime
  const span = Math.max(1, state.timeline.endTime - state.timeline.startTime || rawEnd - rawStart || 1)
  if (rawEnd > viewLimit || rawStart > viewLimit) {
    const padding = Math.max(30, span)
    state.timeline.viewMax = Math.max(viewLimit, rawEnd + padding, rawStart + padding)
    viewLimit = Math.max(totalLength, state.timeline.viewMax || 0)
    if (state.timeline.exportAuto) {
      state.timeline.exportOut = Math.max(state.timeline.exportOut || 0, viewLimit)
    }
  }
  const nextStart = Math.max(0, Math.min(rawStart, viewLimit))
  const nextEnd = Math.max(0, Math.min(rawEnd, viewLimit))
  const currentStart = state.timeline.startTime
  const currentEnd = state.timeline.endTime
  const deltaStart = nextStart - currentStart
  const deltaEnd = nextEnd - currentEnd
  if (Math.abs(deltaStart) < 1e-6 && Math.abs(deltaEnd) < 1e-6) {
    state.timeline.startTime = nextStart
    state.timeline.endTime = nextEnd
    return
  }
  if (state.timeline._animFrame) {
    cancelAnimationFrame(state.timeline._animFrame)
    state.timeline._animFrame = null
  }
  const startTs = performance.now()
  const dur = Math.max(0, duration)
  const ease =
    easing ||
    ((t) => {
      const x = Math.max(0, Math.min(t, 1))
      return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2
    })
  const step = (ts) => {
    const elapsed = ts - startTs
    const progress = dur === 0 ? 1 : Math.min(elapsed / dur, 1)
    const factor = ease(progress)
    state.timeline.startTime = currentStart + deltaStart * factor
    state.timeline.endTime = currentEnd + deltaEnd * factor
    if (progress < 1) {
      state.timeline._animFrame = requestAnimationFrame(step)
    } else {
      state.timeline._animFrame = null
      state.timeline.startTime = nextStart
      state.timeline.endTime = nextEnd
    }
  }
  state.timeline._animFrame = requestAnimationFrame(step)
}

export default {
  currentTime: (state, next) => {
    if (next < 0) return
    state.timeline.currentTime = next

    // Deselect active SmartKey if playhead leaves its frame
    if (state.timeline.activeSmartKey) {
      const t = state.timeline.activeSmartKey.timeAbs
      if (typeof t === 'number' && Math.abs(t - next) > 1e-6) {
        state.timeline.activeSmartKey = null
      }
    }
    if (!state.timeline.activeTrack || !state.timeline.activeStrip)
      return

    state.timeline.activeKeyframe = null
    const fps = state.settings?.fps || 30
    const EPS = 1 / fps / 2
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes.forEach((kf) => {
      const abs = state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ].start + kf.time
      if (Math.abs(abs - next) < EPS)
        state.timeline.activeKeyframe = kf.uuid
    })

    // If lane selection exists, clear it when playhead no longer targets its keyframe.
    if (state.timeline.activeKeyframeSelection) {
      const sel = state.timeline.activeKeyframeSelection
      if (!sel || sel.uuid !== state.timeline.activeKeyframe) {
        state.timeline.activeKeyframeSelection = null
      }
    }
  },
  activeKeyframeSelection: (state, next) => {
    state.timeline.activeKeyframeSelection = next
  },
  currentTimeWindow: (state, { start, end }) => {
    if (start < 0) return
    const totalLength = state.timeline.length || 0
    let viewLimit = Math.max(totalLength, state.timeline.viewMax || 0)
    const rawStart = Number.isFinite(start) ? start : state.timeline.startTime
    const rawEnd = Number.isFinite(end) ? end : state.timeline.endTime
    const span = Math.max(1, state.timeline.endTime - state.timeline.startTime || rawEnd - rawStart || 1)
    if (rawEnd > viewLimit) {
      const padding = Math.max(30, span)
      state.timeline.viewMax = Math.max(viewLimit, rawEnd + padding)
      viewLimit = Math.max(totalLength, state.timeline.viewMax || 0)
    }
    state.timeline.startTime = Math.max(0, Math.min(rawStart, viewLimit))
    state.timeline.endTime = Math.max(state.timeline.startTime, Math.min(rawEnd, viewLimit))
  },
  extendViewMax: (state, next) => {
    const len = state.timeline.length || 0
    const safeNext = Number.isFinite(next) ? next : len
    const padded = Math.max(len, safeNext)
    const current = state.timeline.viewMax || 0
    state.timeline.viewMax = Math.max(current, padded)
    if (state.timeline.exportAuto) {
      state.timeline.exportOut = Math.max(state.timeline.exportOut || 0, Math.min(state.timeline.viewMax, padded))
    }
  },
  animateTimeWindow: (state, payload) => doAnimateTimeWindow(state, payload),
  zoomTimeWindow: (state, { direction, steps = 1, duration = 150 } = {}) => {
    const dir = direction === 'out' ? 'out' : 'in'
    const stepCount = Math.max(1, Math.min(12, Number(steps) || 1))

    const totalLength = state.timeline.length || 0
    const currentStart = state.timeline.startTime || 0
    const currentEnd = state.timeline.endTime || 0
    const currentLen = Math.max(0, currentEnd - currentStart)
    if (totalLength <= 0 || currentLen <= 0) return

    const ratioPerStep = dir === 'in' ? 1 - ZOOM_STEP : 1 + ZOOM_STEP
    let targetLen = currentLen * Math.pow(ratioPerStep, stepCount)
    if (dir === 'in') targetLen = Math.max(ZOOM_MAX_SEC, targetLen)

    // Grow viewMax so zoom-out can extend beyond current total length just like the toolbar buttons.
    const anchor = dir === 'in'
      ? (Number.isFinite(state.timeline.currentTime) ? state.timeline.currentTime : currentStart + currentLen / 2)
      : currentStart + currentLen / 2

    const baseLimit = Math.max(totalLength, state.timeline.viewMax || 0)
    const padding = Math.max(VIEW_MAX_PADDING, targetLen)
    const padded = anchor + targetLen / 2 + padding
    state.timeline.viewMax = Math.max(baseLimit, padded)
    const limit = Math.max(totalLength, state.timeline.viewMax || 0)

    // Clamp targetLen to limit for zoom-out, keep min bound for zoom-in.
    if (limit > 0) {
      if (dir === 'out') targetLen = Math.min(targetLen, limit)
      else targetLen = Math.min(targetLen, limit)
    }
    targetLen = Math.max(dir === 'in' ? ZOOM_MAX_SEC : 0, targetLen)

    let start = anchor - targetLen / 2
    let end = anchor + targetLen / 2

    if (start < 0) {
      start = 0
      end = targetLen
    }
    if (end > limit) {
      end = limit
      start = Math.max(0, limit - targetLen)
    }

    start = Math.max(0, Math.min(start, limit))
    end = Math.max(start, Math.min(end, limit))

    doAnimateTimeWindow(state, { start, end, duration })
  },
  setExportRange: (state, { start, end, auto = false }) => {
    const limit = Math.max(state.timeline.length || 0, state.timeline.viewMax || 0)
    const safeStart = Number.isFinite(start) ? Math.max(0, start) : state.timeline.exportIn || 0
    let safeEnd = Number.isFinite(end) ? Math.max(0, end) : state.timeline.exportOut || limit
    let clampedStart = limit > 0 ? Math.min(safeStart, limit) : safeStart
    let clampedEnd = limit > 0 ? Math.min(Math.max(safeEnd, clampedStart), limit) : Math.max(safeEnd, clampedStart)
    state.timeline.exportIn = clampedStart
    state.timeline.exportOut = clampedEnd
    state.timeline.exportAuto = !!auto
    if (!auto && clampedEnd > state.timeline.viewMax) {
      state.timeline.viewMax = clampedEnd
    }
  },
  setExportIn: (state, start) => {
    const limit = Math.max(state.timeline.length || 0, state.timeline.viewMax || 0)
    const safeStart = Number.isFinite(start) ? Math.max(0, start) : state.timeline.exportIn || 0
    const clamped = limit > 0 ? Math.min(safeStart, limit) : safeStart
    state.timeline.exportIn = clamped
    state.timeline.exportOut = Math.max(clamped, state.timeline.exportOut || clamped)
    state.timeline.exportAuto = false
    if (state.timeline.exportOut > state.timeline.viewMax) {
      state.timeline.viewMax = state.timeline.exportOut
    }
  },
  setExportOut: (state, end) => {
    const limit = Math.max(state.timeline.length || 0, state.timeline.viewMax || 0)
    const safeEnd = Number.isFinite(end) ? Math.max(0, end) : state.timeline.exportOut || 0
    const clamped = limit > 0 ? Math.min(Math.max(safeEnd, state.timeline.exportIn || 0), limit) : Math.max(safeEnd, state.timeline.exportIn || 0)
    state.timeline.exportOut = clamped
    state.timeline.exportAuto = false
    if (clamped > state.timeline.viewMax) {
      state.timeline.viewMax = clamped
    }
  },
  setExportAuto: (state, next) => {
    state.timeline.exportAuto = !!next
    if (state.timeline.exportAuto) {
      const limit = Math.max(state.timeline.length || 0, state.timeline.viewMax || 0)
      state.timeline.exportIn = Math.max(0, Math.min(state.timeline.exportIn || 0, limit))
      state.timeline.exportOut = Math.max(state.timeline.exportIn, Math.min(limit, state.timeline.length || limit))
    }
  },
  tune: (state, next) => {
    state.tune = next
  },
  tuned: (state, next) => {
    state.tuned = next
  },
  calculate: (state, next) => {
    state.calculating = next
  },
  record: (state, next) => {
    state.recording = next
  },
  play: (state, next) => {
    const isStartingPlayback = !!next && !state.playing
    if (typeof window !== 'undefined' && window.vybyTimingDebug) {
      try {
        const err = new Error()
        const stack = (err.stack || '').split('\n').slice(1, 8).join(' | ')
        // eslint-disable-next-line no-console
        console.info('[Store] play mutation', {
          next,
          stack,
          timestamp: (typeof performance !== 'undefined' && performance.now)
            ? performance.now() / 1000
            : Date.now() / 1000,
        })
      } catch (e) { /* noop */ }
    }
    state.playing = next
    if (isStartingPlayback) {
      const t = Number(state.timeline?.currentTime)
      state.lastPlaybackStartTime = Number.isFinite(t) ? t : 0
    }
    if (next) {
      // Clear SmartKey selection when playback starts
      state.timeline.activeSmartKey = null
    }
  },
  activeTrack: (state, next) => {
    /**/
    if (state.timeline.activeTrack !== next) {
      state.timeline.activeStrip = null
      state.timeline.activeKeyframe = null
      state.timeline.activeKeyframeSelection = null
      state.timeline.activeEffect = null
      state.timeline.activeSmartKey = null
      state.tool = []
    }
    /**/
    state.timeline.activeTrack = next
    // Remember the last selected layer track for text insertion workflows.
    try {
      const { toSession } = require('@/etc/storage')
      const track = next ? state.tracks?.[next] : null
      if (track && track.type === 'layer') {
        toSession('ui/text/insertTrack', next)
      }
    } catch (e) { /* noop */ }
    // if (state.timeline.activeTrack !== next)
    // state.timeline.activeEffect = null
  },
  activeStrip: (state, next) => {
    /**/
    if (next !== state.timeline.activeStrip) {
      state.timeline.activeKeyframe = null
      state.timeline.activeKeyframeSelection = null
      state.timeline.activeEffect = null
      state.timeline.activeSmartKey = null
      state.tool = []
    }
    /**/
    // if (state.timeline.activeStrip !== next)
    // state.timeline.activeEffect = null
    state.timeline.activeStrip = next
    const track = Object.keys(state.tracks).find((tuuid) =>
      Object.keys(state.tracks[tuuid].strips).includes(next)
    )
    if (track) {
      const strip = state.tracks[track].strips[next]
      const stripType = `${strip?.asset?.type || ''}`.split('/')[0]
      // Keep regular-view lane focus stable when selecting adjustment layers.
      if (stripType !== 'adjustment') {
        state.tracks[track].depth = strip.depth
      }
    }

    if (!state.timeline.activeTrack || !state.timeline.activeStrip) return
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes.forEach((kf) => {
      if (
        state.tracks[state.timeline.activeTrack].strips[
          state.timeline.activeStrip
        ].start +
          kf.time ===
        state.timeline.currentTime
      )
        state.timeline.activeKeyframe = kf.uuid
    })
  },
  activeKeyframe: (state, next) => {
    state.timeline.activeKeyframe = next
  },
  activeKeyframeProps: (state, update) => {
    // console.log('!!AKP', update)
    if (
      !state.tracks[update.track] ||
      !state.tracks[update.track].strips[update.strip]
    ) {
      log.warn('tool update failed', update)
      return
    }
    const asset = factory.asset(
      state.tracks[update.track].strips[update.strip].asset.uuid
    )
    if (!asset) {
      log.warn('tool update asset not found')
      return
    }
    const strip = state.tracks[update.track].strips[update.strip]
    const isText = !!(strip.asset && strip.asset.type === 'text')
    const settings = state.settings
    const kf = strip.animation.keyframes.find(k => k.uuid === state.timeline.activeKeyframe)
    if (kf && kf.marker) {
      // Phase 2: Marker keyframes are metadata-only. Block property edits.
      return
    }
    const smartOn = !!settings.smartKeyGroupsEnabled
    const ask = state.timeline.activeSmartKey
    const hasTransformSmart = !!((strip.smartKeys || {}).transform)
    // Route gizmo updates to Smart Keys when a transform Smart Key is engaged
    if (smartOn && ask && ask.strip === update.strip && ask.track === update.track && ask.prop === 'transform') {
      const extract = asset.toolUpdate(update) || {}
      const mergeMatrix = (base, next) => ({
        position: {
          x: typeof next['position.x'] === 'number' ? next['position.x'] : base?.position?.x,
          y: typeof next['position.y'] === 'number' ? next['position.y'] : base?.position?.y,
          z: typeof next['position.z'] === 'number' ? next['position.z'] : base?.position?.z,
        },
        rotation: {
          x: typeof next['rotation.x'] === 'number' ? next['rotation.x'] : base?.rotation?.x,
          y: typeof next['rotation.y'] === 'number' ? next['rotation.y'] : base?.rotation?.y,
          z: typeof next['rotation.z'] === 'number' ? next['rotation.z'] : base?.rotation?.z,
        },
        scale: {
          x: typeof next['scale.x'] === 'number' ? next['scale.x'] : base?.scale?.x,
          y: typeof next['scale.y'] === 'number' ? next['scale.y'] : base?.scale?.y,
          z: typeof next['scale.z'] === 'number' ? next['scale.z'] : base?.scale?.z,
        }
      })
      const curr = (strip.smartKeys && strip.smartKeys.transform) || {
        start: { position: { x: 0, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0 }, scale: { x: 1, y: 1, z: 1 } },
        left: { position: { x: 0, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0 }, scale: { x: 1, y: 1, z: 1 } },
        right: { position: { x: 0, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0 }, scale: { x: 1, y: 1, z: 1 } },
        end: { position: { x: 0, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0 }, scale: { x: 1, y: 1, z: 1 } },
        offsets: { entry: 0, exit: 0 }, links: { rightToLeft: false }, sustainEasing: 'Linear.None'
      }
      const part = ask.part // 'start' | 'left' | 'right' | 'end'
      const nextPart = mergeMatrix(curr[part] || curr.left, extract)
      const linked = !!(curr.links && curr.links.rightToLeft)
      let nextLinks = { ...(curr.links || {}) }
      if (linked && part === 'right') {
        nextLinks.rightToLeft = false
      }
      const next = {
        ...curr,
        [part]: nextPart,
        ...(linked && part === 'left' ? { right: { ...nextPart } } : {}),
        links: nextLinks
      }
      state.tracks[update.track].strips[update.strip].smartKeys = {
        ...(strip.smartKeys || {}),
        transform: next
      }
      state.tuned = false
      return
    }
    // Gate legacy keyframe writes for transform props when transform Smart Keys exist
    if (smartOn && hasTransformSmart) {
      const transformAxes = [
        'position.x','position.y','position.z',
        'rotation.x','rotation.y','rotation.z',
        'scale.x','scale.y','scale.z'
      ]
      const touchesTransform = Object.keys(update || {}).some(k => transformAxes.includes(k))
      if (touchesTransform) return
    }
    const extract = asset.toolUpdate(update)
    const upd = {}
    const touched = Array.isArray(update.changes) ? update.changes : []
    const isAxisField = (code) => /^(position|rotation|scale)\.(x|y|z)$/.test(code)
    const changedAxes = touched.filter(isAxisField)
    if (changedAxes.length) {
      changedAxes.forEach(axis => { if (typeof extract[axis] === 'number') upd[axis] = extract[axis] })
      if (!Object.keys(upd).length) Object.assign(upd, extract)
    } else {
      Object.assign(upd, extract)
    }

    const activeTextSmartPart =
      ask &&
      ask.track === update.track &&
      ask.strip === update.strip &&
      (ask.prop === 'transform' || TEXT_SMART_TRANSFORM_PROPS.has(ask.prop))
        ? ask.part
        : null

    if (isText) {
      const baseValues = {
        ...(asset?.defaultProps || {}),
        ...((strip.animation && typeof strip.animation.values === 'object') ? strip.animation.values : {}),
      }
      const synced = syncTextTransformToSmartKeys(strip, upd, {
        part: activeTextSmartPart,
        baseValues,
        tool: update.tool,
        delta: update.delta,
      })
      if (synced) {
        state.tuned = false
        return
      }
    }

    // Phase 2 (V2 semantics): if the strip has never had any keyframes, treat edits as base-value edits.
    // Only after the user has manually added at least one keyframe do we begin auto-writing keyframes.
    const kfs = Array.isArray(strip.animation?.keyframes) ? strip.animation.keyframes : []
    const hasAnyUserKeyframes = kfs.length > 0
    if (!hasAnyUserKeyframes) {
      const base = (strip.animation && typeof strip.animation.values === 'object') ? strip.animation.values : {}
      state.tracks[update.track].strips[update.strip].animation.values = {
        ...base,
        ...upd
      }
      asset.emit('updateAnimation', state.tracks[update.track].strips[update.strip].animation)
      state.tuned = false
      return
    }

    // Ensure there is a keyframe at current time when auto-writing
    let kfi = strip.animation.keyframes.findIndex(
      (el) => el.uuid === state.timeline.activeKeyframe
    )
    if (kfi < 0) {
      const tAbs = state.timeline.currentTime
      const tRel = Math.max(0, Math.min(strip.length, tAbs - strip.start))
      const nextKf = { uuid: Uuid(), type: 'point', removable: true, time: tRel, delay: {}, props: {}, easing: {} }
      state.tracks[update.track].strips[update.strip].animation.keyframes.push(nextKf)
      state.tracks[update.track].strips[update.strip].animation.keyframes.sort((a, b) => a.time - b.time)
      kfi = strip.animation.keyframes.findIndex((el) => Math.abs(el.time - tRel) < 1e-6)
      if (kfi >= 0) state.timeline.activeKeyframe = strip.animation.keyframes[kfi].uuid
    }
    const activeKf = strip.animation.keyframes[kfi]
    Object.keys(upd).forEach((code) => {
      state.tracks[update.track].strips[update.strip].animation.keyframes[
        kfi
      ].props[code] = upd[code]
      if (typeof activeKf.delay[code] === 'undefined') {
        state.tracks[update.track].strips[update.strip].animation.keyframes[
          kfi
        ].delay[code] = 0
        state.tracks[update.track].strips[update.strip].animation.keyframes[
          kfi
        ].easing[code] = 'Linear.None'
      }
    })
    asset.emit(
      'updateAnimation',
      state.tracks[update.track].strips[update.strip].animation
    )
    state.tuned = false
  },
  activeKeyframePropsPanel: (state, update) => {
    const strip =
      state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ]
    // state.tracks[state.timeline.activeTrack].strips[state.timeline.activeStrip].asset.uuid
    if (!strip) return
    // Text strips always use SmartKeys for numeric envelope props (even when SmartKeys are globally disabled).
    // Prevent legacy keyframe/value writes for smart-managed props to avoid "no-op" UI edits.
    try {
      const isText = !!(strip.asset && strip.asset.type === 'text')
      const smart = (strip.smartKeys || {})
      if (isText && smart && Object.keys(smart).length) {
        const transformAxes = [
          'position.x','position.y','position.z',
          'rotation.x','rotation.y','rotation.z',
          'scale.x','scale.y','scale.z'
        ]
        const touchesSmart = Object.keys(update || {}).some((k) =>
          transformAxes.includes(k) || k === 'opacity'
        )
        if (touchesSmart) return
      }
    } catch (e) { /* ignore text-smart gating errors */ }
    // Gate legacy keyframe writes for transform when a transform Smart Key group exists
    try {
      const hasTransformSmart = !!((strip.smartKeys || {}).transform)
      const smartOn = !!state.settings.smartKeyGroupsEnabled
      if (smartOn && hasTransformSmart) {
        const transformAxes = [
          'position.x','position.y','position.z',
          'rotation.x','rotation.y','rotation.z',
          'scale.x','scale.y','scale.z'
        ]
        const touchesTransform = Object.keys(update || {}).some(k => transformAxes.includes(k))
        if (touchesTransform) return
      }
    } catch (e) { /* ignore gating errors */ }
    const asset = factory.asset(strip.asset.uuid)
    if (!asset) {
      log.warn('tools panel update asset not found')
      return
    }
    let kfi = strip.animation.keyframes.findIndex(
      (el) => el.uuid === state.timeline.activeKeyframe
    )
    // Phase 2 (V2 semantics): if no keyframes exist yet, editing via the right panel updates base values
    // instead of implicitly creating the first keyframe.
    const hasAnyUserKeyframes = Array.isArray(strip.animation?.keyframes) && strip.animation.keyframes.length > 0
    if (kfi < 0 && !hasAnyUserKeyframes) {
      const base = (strip.animation && typeof strip.animation.values === 'object') ? strip.animation.values : {}
      state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ].animation.values = {
        ...base,
        ...update
      }
      asset.emit('updateAnimation', state.tracks[state.timeline.activeTrack].strips[state.timeline.activeStrip].animation)
      state.tuned = false
      return
    }
    // If keys already exist, keep existing behavior: ensure keyframe at current time for edits
    if (kfi < 0) {
      const tAbs = state.timeline.currentTime
      const tRel = Math.max(0, Math.min(strip.length, tAbs - strip.start))
      const uuid = Uuid()
      const next = jclone(strip.animation.keyframes)
      next.push({ uuid, type: 'point', removable: true, time: tRel, delay: {}, props: {}, easing: {} })
      state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ].animation.keyframes = next.sort((a, b) => a.time - b.time)
      state.timeline.activeKeyframe = uuid
      kfi = strip.animation.keyframes.findIndex((el) => el.uuid === uuid)
    }
    if (!state.tracks[state.timeline.activeTrack]) {
      log.warn(
        'No state.tracks[state.timeline.activeTrack]',
        state.tracks[state.timeline.activeTrack]
      )
      return
    }
    Object.keys(update).forEach((code) => {
      state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ].animation.keyframes[kfi].props[code] = update[code]
    })
    asset.emit(
      'updateAnimation',
      state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ].animation
    )
    // console.log('PROPS UPDATED?', strip.animation.keyframes[kfi])
    state.tuned = false
  },
  modifyKeyframePropED: (
    state,
    { tuuid, suuid, kuuid, prop, delay, easing }
  ) => {
    if (!state.tracks[tuuid]) return
    if (!state.tracks[tuuid].strips[suuid]) return
    const kfi = state.tracks[tuuid].strips[
      suuid
    ].animation.keyframes.findIndex((kf) => kf.uuid === kuuid)
    if (kfi < 0) return
    state.tracks[tuuid].strips[suuid].animation.keyframes[kfi].delay[prop] =
      delay
    state.tracks[tuuid].strips[suuid].animation.keyframes[kfi].easing[prop] =
      easing
    state.tuned = false
  },
  ultraExpand: (state, payload) => {
    const p = payload && typeof payload === 'object' ? payload : {}
    const open = !!p.open
    const trackUuid = typeof p.trackUuid === 'string' ? p.trackUuid : null
    state.timeline.ultraExpand = {
      open,
      trackUuid: open ? trackUuid : null,
    }
  },
}

